import {
  PARTICIPATION_NOTE_MAX,
  type ParticipationEligibility,
  type ParticipationRequestType,
  participationCreateSchema,
} from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Select, Textarea, useToast } from '@fruuit/ui'
import { type FormEvent, useState } from 'react'
import { api } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { dateTime } from '../engagements/format'
import { type FieldErrors, serverErrors, validate } from '../engagements/forms'
import { LANE_D_KEYS } from '../engagements/queries'

export interface RequestParticipationDialogProps {
  open: boolean
  onClose: () => void
  engagementId: string
  companyName: string
  eligibility: ParticipationEligibility
  /** A specific meeting (from the meeting page); omit for a case-level request. */
  meeting?: { id: string; title: string; starts_at: string } | null
}

/**
 * Request participation (P-PART-001/-002). The request types offered come from the server's
 * eligibility (`available_types`), computed with the server clock. Nothing is sent outside
 * this workspace; an acknowledgment is not a booking (PART-021).
 */
export function RequestParticipationDialog({
  open,
  onClose,
  engagementId,
  companyName,
  eligibility,
  meeting,
}: RequestParticipationDialogProps) {
  const types = meeting
    ? (['Join a future company meeting'] as ParticipationRequestType[])
    : eligibility.available_types
  const [type, setType] = useState<string>(types[0] ?? '')
  const [note, setNote] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const { toast } = useToast()

  function close() {
    setErrors({})
    onClose()
  }

  const mutation = useApiMutation(
    (input: Record<string, unknown>, { requestId }) =>
      api.post<{ deduplicated?: boolean }>('/participation-requests', input, { requestId }),
    {
      invalidate: LANE_D_KEYS,
      onSuccess: (data) => {
        setNote('')
        close()
        toast({
          tone: 'success',
          message: data?.deduplicated
            ? 'You already had an open request for this; we kept that one.'
            : 'Request saved. The engagement manager will respond here.',
        })
      },
      onError: (error) => setErrors(serverErrors(error).fields),
    },
  )
  const formError = mutation.error ? serverErrors(mutation.error).message : null

  function submit(event: FormEvent) {
    event.preventDefault()
    const checked = validate(participationCreateSchema, {
      engagement_id: engagementId,
      request_type: type || types[0],
      note,
      ...(meeting ? { meeting_id: meeting.id } : {}),
    })
    if (!checked.ok) {
      setErrors(checked.errors)
      return
    }
    setErrors({})
    mutation.mutate(checked.data)
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Request participation"
      description={
        meeting
          ? `${meeting.title} · ${dateTime(meeting.starts_at)}`
          : `Ask to take part in the engagement with ${companyName}.`
      }
      footer={
        <>
          <DialogCancel />
          <Button
            type="submit"
            form="participation-form"
            loading={mutation.isPending}
            disabled={types.length === 0}
          >
            Send request
          </Button>
        </>
      }
    >
      <form id="participation-form" onSubmit={submit} noValidate className="space-y-4">
        <Select
          label="What would you like?"
          required
          options={types.map((value) => ({ value, label: value }))}
          value={type || types[0] || ''}
          onChange={(event) => setType(event.target.value)}
          error={errors.request_type}
        />
        <Textarea
          label="Note for the engagement manager"
          hint="What you would like to raise, and any constraints on timing."
          required
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={PARTICIPATION_NOTE_MAX}
          error={errors.note}
        />
        <Callout tone="note">
          No email, calendar invitation or company contact is sent. An acknowledgment is a planning
          step, not a confirmed place at a meeting.
        </Callout>
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
